import {
  BellNotification,
  COLORS,
  Icon,
  SPACING,
  Text,
  UnstyledButton,
  mediaTabletAndUp,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {useHasNewNotification} from '../NotificationCenter/components/utils/useHasNewNotification';
import {WalletType} from './utils/useActiveTab';
import {NormalizedWalletTab} from './utils/useNormalizeTabs';

const TIMING = '0.3s';

export const WalletHeader: React.FC<{
  activeTab: WalletType;
  onTabClick: (value: WalletType) => void;
  tabs: NormalizedWalletTab[];
  showSolidHeader: boolean;
}> = ({activeTab, onTabClick, tabs, showSolidHeader}) => {
  const activeTabData = tabs.find(tab => tab.value === activeTab);
  const inactiveTabs = tabs.filter(tab => tab.value !== activeTab);

  const {notificationsCount, setWasNotificationCenterOpened} =
    useHasNewNotification();

  useEffect(() => {
    if (activeTab === 'notificationCenter') {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setWasNotificationCenterOpened(true);
    }
  }, [activeTab, setWasNotificationCenterOpened]);

  if (!activeTabData) {
    return null;
  }

  return (
    <TabsContainer>
      {tabs.map(tab => {
        const isActive = tab.value === activeTab;
        const dataTestId = `walletHeader-${tab.value}-${
          isActive ? 'active' : 'inactive'
        }`;
        const inactiveIndex = isActive
          ? -1
          : inactiveTabs.findIndex(t => t.value === tab.value);
        return (
          <Tab
            key={tab.value}
            data-testid={dataTestId}
            data-active={isActive}
            aria-label={tab.title}
            $inactiveIndex={inactiveIndex}
            onClick={() => !isActive && onTabClick(tab.value)}
          >
            {tab.value === 'notificationCenter' ? (
              <BellNotification
                hasNewNotification={notificationsCount > 0}
                color={showSolidHeader || isActive ? 'default' : 'white'}
              />
            ) : (
              <Icon asset={tab.icon} size="S" fill={COLORS['Fill.White']} />
            )}
            <TabLabel
              data-testid={`${dataTestId}-label`}
              data-visible={isActive}
              color="inherit"
              size="px20"
              weight="mediumHeavy"
            >
              {tab.title}
            </TabLabel>
          </Tab>
        );
      })}
    </TabsContainer>
  );
};

const TabsContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const Tab = styled(UnstyledButton)<{$inactiveIndex: number}>`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  height: 100%;
  align-items: center;
  white-space: nowrap;
  z-index: 1;
  will-change: transform, left, right;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: transform ${TIMING} cubic-bezier(0.4, 0, 0.2, 1),
    left ${TIMING} cubic-bezier(0.4, 0, 0.2, 1),
    right ${TIMING} cubic-bezier(0.4, 0, 0.2, 1);

  ${tabletAndUpLinaria(`
    left: 0;
    right: unset;
  `)};

  // Active tab, centered in container (considering back or close icon)
  &[data-active='true'] {
    gap: ${SPACING['X.Md']};
    left: 50%;
    right: auto;

    // Mobile: back button on left
    // Shift left by 24px (icon width) + half of header gap (8px) = 32px total
    transform: translateX(calc(-50% - 24px - calc(${SPACING['X.Lg']} / 2)));

    @media ${mediaTabletAndUp} {
      // Desktop: close button on right
      // Shift right by 24px (icon width) - half of header gap (8px) = 16px total
      transform: translateX(calc(-50% + 24px - calc(${SPACING['X.Lg']} / 2)));
    }
  }

  &[data-active='false'] {
    transform: ${props => `translateX(-${props.$inactiveIndex * 32}px)`};

    @media ${mediaTabletAndUp} {
      transform: ${props => `translateX(${props.$inactiveIndex * 32}px)`};
    }
  }
`;

const TabLabel = styled(Text)`
  white-space: nowrap;
  display: inline-block;
  overflow: hidden;

  &[data-visible='true'] {
    opacity: 1;
    max-width: 200px;
    // eslint-disable-next-line local-rules/css-layout-animation
    transition: opacity ${TIMING} cubic-bezier(0.4, 0, 0.2, 1),
      max-width ${TIMING} cubic-bezier(0.4, 0, 0.2, 1);
  }

  &[data-visible='false'] {
    opacity: 0;
    max-width: 0;
    // eslint-disable-next-line local-rules/css-layout-animation
    transition: opacity ${TIMING} cubic-bezier(0.4, 0, 0.2, 1),
      max-width ${TIMING} cubic-bezier(0.4, 0, 0.2, 1);
  }
`;
